import { FC, useMemo, useState } from 'react'
import { Box, Dialog, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material'
import { LineChart } from '@mui/x-charts/LineChart'
import { useTranslation } from 'react-i18next'
import CloseIcon from '@mui/icons-material/Close'


interface RankHistoryEntry {
  date: string
  rankGroup: number | null
  rankAiOverview: number | null
}

interface TrackingHistoryTrendChartProps {
  data: RankHistoryEntry[]
  options?: any
}

/**
 * Un componente que muestra la evolución del ranking de una palabra clave.
 * Al hacer clic en el icono, abre un popup con un gráfico detallado.
 */
const TrackingHistoryTrendChart: FC<TrackingHistoryTrendChartProps> = ({ data, options }) => {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);

  const handleClickOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  /**
   * Process the data. Memo to optimize performance.
   */
  const chartData = useMemo(() => {
    if (!data) {
      return null;
    }

    // Filter and convert data to the format expected by the chart.
    const sortedData = data
      .map(item => ({
        date: new Date(item.date),
        rankGroup: item.rankGroup === -1 ? null : item.rankGroup,
        rankAiOverview: item.rankAiOverview === -1 ? null : item.rankAiOverview,
      }))
      .filter(item => !isNaN(item.date.getTime()))
      .sort((a, b) => a.date.getTime() - b.date.getTime())

    if (sortedData.length < 1) {
      return null;
    }

    const xAxisData = [{
      data: sortedData.map(item => item.date),
      scaleType: 'time',
      valueFormatter: (date: Date) => date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' }),
      tickInterval: (() => {
        const validDates = (data || [])
          .map(item => item?.date ? new Date(item.date) : null)
          .filter((date): date is Date => date instanceof Date && !isNaN(date.getTime()));

        if (validDates.length === 0) {
          return []
        }

        const minDate = new Date(Math.min(...validDates.map(d => d.getTime())));
        minDate.setUTCHours(0, 0, 0, 0)
        const maxDate = new Date(Math.max(...validDates.map(d => d.getTime())));
        maxDate.setUTCHours(0, 0, 0, 0)

        const ticks = []
        let currentDate = new Date(minDate);

        while (currentDate <= maxDate) {
          ticks.push(new Date(currentDate))
          currentDate.setUTCDate(currentDate.getUTCDate() + 1);
        }

        return ticks;
      })()
    }];


    const allSeries = [];

    // Serie para rankAiOverview (línea azul)
    if(options.showAiOverview) {
      allSeries.push({
        data: sortedData.map(item => item.rankAiOverview),
        label: t('headerName.ai'),
        color: '#2196f3',
        showMark: true,
        valueFormatter: (value: number | null) => (value === null ? 'N/A' : value.toString()),
      });
    }

    // Series para los segmentos de rankGroup (líneas de colores)
    let hasAbsoluteRankData = false;
    for (let i = 1; i < sortedData.length; i++) {
        const prev = sortedData[i - 1];
        const curr = sortedData[i];

        if (prev.rankGroup != null && curr.rankGroup != null) {
            hasAbsoluteRankData = true;
            let color = '#000000';
            if (curr.rankGroup < prev.rankGroup) color = '#4caf50'; // Green: improvement
            if (curr.rankGroup > prev.rankGroup) color = '#f44336'; // Red: worst

            const segmentData = sortedData.map((_, index) => {
                if (index === i - 1) return prev.rankGroup;
                if (index === i) return curr.rankGroup;
                return null;
            });
            allSeries.push({ data: segmentData, label: t('headerName.position'),  color, showMark: true, id: `segment-${i}` });
        }
    }

    return { xAxis: xAxisData, series: allSeries };
  }, [data, t]);

  // If not enough data, show a message.
  if (!data || data.length < 2) {
    return (
      <Box sx={{ p: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <Typography variant="caption" color="text.secondary">{t('insufficientData')}</Typography>
      </Box>
    );
  }

  let rankLineColor = '#BBBCBD';
  if (data.length >= 2) {
    const lastItem = data[0];
    const previousItem = data[1];

    if (lastItem.rankGroup !== null && previousItem.rankGroup !== null) {
      if (lastItem.rankGroup < previousItem.rankGroup) {
        rankLineColor = 'green'
      } else if (lastItem.rankGroup > previousItem.rankGroup) {
        rankLineColor = 'red'
      }
    }
  }

  const rankValues = data
    .map(item => item.rankGroup)
    .filter(rank => rank !== null);
  const dataMin = Math.min(...rankValues, data[0].rankAiOverview);
  const dataMax = Math.max(...rankValues, data[0].rankAiOverview);



  return (
    <>
      <Box onClick={handleClickOpen} sx={{ cursor: 'pointer' }}>
        <LineChart
          dataset={(data || [])
            .map(item => ({
              date: new Date(item.date),
              rankGroup: item.rankGroup === -1 ? null : item.rankGroup,
              rankAiOverview: item.rankAiOverview  < 1 ? null : item.rankAiOverview
            }))
            .filter(item => !isNaN(item.date.getTime()))
            .sort((a, b) => a.date.getTime() - b.date.getTime())
          }
          margin={{ top: 5, right: -30, bottom: 5, left: -20 }}
          slotProps={{
            legend: { hidden: true },
            axisLine: { style: { display: 'none'  } },
            axisTick: { style: { display: 'none'  } }
          }}
          xAxis={[{
            dataKey: 'date',
            scaleType: 'time',
            disableTicks: true,
            disableLine: true,
            tickLabelStyle: { display: 'none' },
            valueFormatter: (date: Date) => date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit', year: '2-digit' })
          }]}
          yAxis={[{
            hideTooltip: true,
            disableTicks: true,
            disableLine: true,
            tickLabelStyle: { display: 'none' },
            reverse: true
          }]}
          series={[
            { dataKey: 'rankGroup', label: t('headerName.position'), color: rankLineColor, showMark: false },
            // show only when showAiOverview is true
            ...(options.showAiOverview ? [{ dataKey: 'rankAiOverview', label: t('headerName.ai'), color: '#6154E8', showMark: false }] : [])
          ]}
          height={60}
          width={180}
          />
      </Box>

      <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth disableScrollLock={true} >
        <DialogTitle>
          {t('trackingHistoryTrend')}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>

        </DialogTitle>
        <DialogContent>
          {chartData ? (
            <Box sx={{ height: 400, width: '100%', pt: 2 }}>
              <LineChart
                xAxis={chartData.xAxis}
                series={chartData.series}
                yAxis={[{
                  reverse: true,
                  min: dataMin - 5,
                  max: dataMax + 5
                }]}
                margin={{ top: 20, right: 40, bottom: 30, left: 40 }}
                slotProps={{
                  legend: { hidden: true }
                }}

                tooltip={{
                  trigger: 'item'
                }}

              />
            </Box>
          ) : (
            <Typography>{t('noDataAvailable')}</Typography>
          )}
        </DialogContent>
      </Dialog>

    </>

  );
};

export default TrackingHistoryTrendChart;